import { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { FileText, Download, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Backward-compat: flat ya da nested pdf_file_id destegi.
 * @param {Object} lesson
 * @returns {string|null}
 */
function getPdfFileId(lesson) {
  return lesson.pdf_file_id || lesson.pdf?.pdf_file_id || null;
}
function getPdfFilename(lesson) {
  return lesson.pdf_filename || lesson.pdf?.pdf_filename || 'PDF Material';
}

/**
 * PdfLesson — PDF materyali goruntuler + tamamla butonu.
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function PdfLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const lessonId = lesson.lesson_id;
  const [pdfOpened, setPdfOpened] = useState(false);
  const [completing, setCompleting] = useState(false);
  const isCompleted = completedLessonIds.includes(lessonId);

  const fileId = getPdfFileId(lesson);
  const filename = getPdfFilename(lesson);
  // PDF dosyasi hic yuklenmemisse (admin hatasi vb.) ogrenci kilitlenmesin —
  // TextLesson'daki "kanit hic set edilemiyor" sinifindaki hatanin PDF esdegeri.
  const noFile = !fileId;

  const [pdfLoading, setPdfLoading] = useState(false);
  const [matLoading, setMatLoading] = useState({});

  /**
   * Korumalı /api/files-by-id/{id} endpoint'ini auth token ile açar.
   * Raw <a href> token göndermediği için 401 alıyordu — blob proxy ile düzeltildi.
   */
  const openPdfWithAuth = async (fileIdToOpen, _downloadFilename) => {
    try {
      const res = await axios.get(
        `${API}/files-by-id/${fileIdToOpen}`,
        { headers: authHeaders(), responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return true;
    } catch (err) {
      const status = err?.response?.status;
      let msg = lang === 'tr' ? 'Dosya açılamadı.' : 'Could not open file.';
      if (status === 401 || status === 403) {
        msg = lang === 'tr'
          ? 'Oturum süresi doldu. Lütfen tekrar giriş yapın.'
          : 'Session expired. Please log in again.';
      } else {
        try {
          const blob = err?.response?.data;
          if (blob instanceof Blob) {
            const text = await blob.text();
            const json = JSON.parse(text);
            const detail = json?.detail;
            if (typeof detail === 'object') {
              msg = (lang === 'tr' ? detail.message_tr : detail.message_en) || msg;
            } else if (typeof detail === 'string') {
              msg = detail;
            }
          }
        } catch { /* parse başarısız — varsayılan mesaj */ }
      }
      toast.error(msg);
      return false;
    }
  };

  const handleOpenMainPdf = async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    const opened = await openPdfWithAuth(fileId, filename);
    if (opened) setPdfOpened(true);
    setPdfLoading(false);
  };

  const handleOpenMaterial = async (matFileId, matFilename, idx) => {
    if (matLoading[idx]) return;
    setMatLoading(prev => ({ ...prev, [idx]: true }));
    await openPdfWithAuth(matFileId, matFilename);
    setMatLoading(prev => ({ ...prev, [idx]: false }));
  };

  const handleManualComplete = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, pdf_opened: noFile ? true : pdfOpened },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <>
      {/* PDF card */}
      {noFile ? (
        <div className="card-clay p-8 text-center text-[var(--text-muted)]" data-testid="pdf-no-file">
          <FileText size={40} className="mx-auto mb-3 text-gray-400" />
          <p className="font-semibold">
            {lang === 'tr' ? 'PDF materyali bulunamadı.' : 'No PDF material found.'}
          </p>
          <p className="text-xs mt-2">
            {lang === 'tr'
              ? 'Sorun bizde — dersi tamamlayarak devam edebilirsin.'
              : "This is on us — you can complete this lesson to continue."}
          </p>
        </div>
      ) : (
        <section className="card-clay p-5 mb-6" data-testid="lesson-pdf-material" aria-label={lang === 'tr' ? 'PDF Materyali' : 'PDF Material'}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center" aria-hidden="true">
              <FileText size={24} className="text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm">{filename}</h3>
              <p className="text-xs text-[var(--text-muted)]">
                {lang === 'tr' ? 'Ders materyali' : 'Lesson material'}
              </p>
            </div>
            <button
              type="button"
              onClick={handleOpenMainPdf}
              disabled={pdfLoading}
              className="btn-tactile px-4 py-2 text-sm bg-white border-[var(--border-light)] flex items-center gap-2 disabled:opacity-60"
              data-testid="download-pdf-btn"
              title={lang === 'tr' ? `${filename} dosyasını göster` : `View ${filename}`}
            >
              <Download size={16} />
              {pdfLoading
                ? (lang === 'tr' ? 'Yükleniyor...' : 'Loading...')
                : (lang === 'tr' ? 'PDF Görüntüle' : 'View PDF')}
            </button>
          </div>

          {/* PDF acildi mi? */}
          <div className="mt-3 text-xs font-semibold" data-testid="pdf-open-status">
            {pdfOpened ? (
              <span className="text-green-600">
                <CheckCircle size={12} className="inline mr-1" />
                {lang === 'tr' ? 'PDF acildi' : 'PDF opened'}
              </span>
            ) : (
              <span className="text-amber-600">
                {lang === 'tr' ? "Dersi tamamlamak icin PDF'i aciniz" : 'Open the PDF to complete this lesson'}
              </span>
            )}
          </div>
        </section>
      )}

      {/* Additional materials list (mevcut deger korundu) */}
      {lesson.materials && lesson.materials.length > 0 && (
        <div className="card-clay p-5 mb-6">
          <h4 className="font-bold text-sm mb-3">
            {lang === 'tr' ? 'Ek Materyaller' : 'Additional Materials'}
          </h4>
          <div className="space-y-2">
            {lesson.materials.map((m, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleOpenMaterial(m.file_id, m.filename, idx)}
                disabled={!!matLoading[idx]}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left disabled:opacity-60"
              >
                <FileText size={18} className="text-red-500" />
                <span className="font-semibold text-sm flex-1">{m.filename}</span>
                <Download size={16} className="text-[var(--text-muted)]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Complete bar */}
      {!isCompleted && (
        <div className="card-clay p-4 mb-6 flex items-center justify-between gap-4" data-testid="complete-lesson-bar">
          <div className="text-sm font-semibold text-[var(--text-muted)]">
            {lang === 'tr' ? 'Dersi bitirdin mi?' : 'Finished this lesson?'}
          </div>
          <button
            onClick={handleManualComplete}
            disabled={completing || (!noFile && !pdfOpened)}
            className="btn-success px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="complete-lesson-btn"
            title={(!noFile && !pdfOpened) ? (lang === 'tr' ? "Once PDF'i aciniz" : 'Open the PDF first') : undefined}
          >
            <CheckCircle size={16} />
            {completing ? '...' : (lang === 'tr' ? 'Dersi Tamamla' : 'Complete Lesson')}
          </button>
        </div>
      )}
      {isCompleted && (
        <div className="card-clay p-4 mb-6 text-center bg-green-50 border-green-200" data-testid="lesson-already-completed">
          <CheckCircle size={20} className="inline text-green-600 mr-2" />
          <span className="font-bold text-sm text-green-700">
            {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
          </span>
        </div>
      )}
    </>
  );
}
